import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Palette, Upload, Globe, Type, MessageCircle, Save, Eye, Image, Sparkles, DollarSign, Gift, ArrowDownCircle, ArrowUpCircle, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TenantSettingsPage() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState('branding');
  const fileRef = useRef(null);
  const [mailStatus, setMailStatus] = useState(null);
  const [mailSaving, setMailSaving] = useState(false);
  const [mailConfig, setMailConfig] = useState({
    smtp_host: 'smtppro.zoho.in',
    smtp_port: '587',
    smtp_user: 'verify@playemp.vip',
    smtp_pass: '',
    smtp_from: 'verify@playemp.vip',
    smtp_from_name: 'Starlight',
  });
  const [testEmail, setTestEmail] = useState('');

  // Branding state
  const [name, setName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#D4AF37');
  const [secondaryColor, setSecondaryColor] = useState('#10B981');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [supportEmail, setSupportEmail] = useState('');

  // Rules state
  const [rules, setRules] = useState({
    deposit_rules: { min_amount: 10, max_amount: 10000 },
    withdrawal_rules: { min_amount: 20, max_amount: 5000 },
    bonus_rules: { first_deposit_bonus_pct: 100, signup_bonus_xp: 50, referral_bonus_xp: 25 },
    support_channels: { facebook: '', whatsapp: '', telegram: '', messenger: '' },
  });

  useEffect(() => {
    fetchTenant();
    fetchMailStatus();
  }, []);

  const fetchTenant = async () => {
    try {
      const res = await api.get('/api/admin/my-tenant');
      const t = res.data.tenant;
      setTenant(t);
      setName(t.name || '');
      setPrimaryColor(t.branding?.primary_color || '#D4AF37');
      setSecondaryColor(t.branding?.secondary_color || '#10B981');
      setWelcomeMessage(t.branding?.welcome_message || '');
      setSupportEmail(t.settings?.support_email || '');
      setMailConfig(prev => ({
        ...prev,
        smtp_from_name: t.name || prev.smtp_from_name,
      }));
      if (!testEmail && t.settings?.support_email) {
        setTestEmail(t.settings.support_email);
      }
      setRules({
        deposit_rules: t.settings?.deposit_rules || { min_amount: 10, max_amount: 10000 },
        withdrawal_rules: t.settings?.withdrawal_rules || { min_amount: 20, max_amount: 5000 },
        bonus_rules: t.settings?.bonus_rules || { first_deposit_bonus_pct: 100, signup_bonus_xp: 50, referral_bonus_xp: 25 },
        support_channels: { facebook: '', whatsapp: '', telegram: '', messenger: '', ...(t.settings?.support_channels || {}) },
      });
    } catch { toast.error('Failed to load tenant info'); }
    finally { setLoading(false); }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      await api.patch('/api/admin/my-tenant/branding', {
        name, primary_color: primaryColor, secondary_color: secondaryColor,
        welcome_message: welcomeMessage, support_email: supportEmail,
      });
      toast.success('Branding saved!');
      fetchTenant();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      await api.patch('/api/admin/my-tenant/rules', rules);
      toast.success('Rules saved!');
      fetchTenant();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingRules(false); }
  };

  const fetchMailStatus = async () => {
    try {
      const res = await api.get('/api/admin/smtp/status');
      const s = res.data || {};
      setMailStatus(s);
      setMailConfig(prev => ({
        ...prev,
        smtp_host: s.smtp_host || prev.smtp_host,
        smtp_port: String(s.smtp_port || prev.smtp_port),
        smtp_user: s.smtp_user || prev.smtp_user,
        smtp_from: s.smtp_from || prev.smtp_from,
        smtp_from_name: s.smtp_from_name || prev.smtp_from_name,
      }));
    } catch {}
  };

  const saveMailConfig = async () => {
    setMailSaving(true);
    try {
      await api.put('/api/admin/smtp/config', mailConfig);
      toast.success('Zoho mail saved for this tenant');
      setMailConfig(prev => ({ ...prev, smtp_pass: '' }));
      await fetchMailStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Zoho mail');
    } finally {
      setMailSaving(false);
    }
  };

  const sendTestMail = async () => {
    if (!testEmail) return toast.error('Enter a test email');
    try {
      const res = await api.post('/api/admin/smtp/test', { email: testEmail });
      if (res.data?.success) toast.success(res.data?.message || 'Test email sent');
      else toast.error(res.data?.message || res.data?.error || 'Test failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/admin/my-tenant/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo uploaded!');
      fetchTenant();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const getLogoUrl = () => {
    const url = tenant?.branding?.logo_url;
    if (!url) return null;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const updateRule = (section, key, value) => {
    setRules(p => ({ ...p, [section]: { ...p[section], [key]: value } }));
  };

  const sections = [
    { id: 'branding', label: 'Branding & Theme', icon: Palette },
    { id: 'support', label: 'Support Channels', icon: MessageCircle },
    { id: 'mail', label: 'Zoho Mail', icon: Mail },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5" data-testid="tenant-settings-page">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D4AF37]" /> Customize Your Platform</h2>
        <p className="text-sm text-gray-400 mt-0.5">Personalize branding, rules, and support for your players</p>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${activeSection === s.id ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-gray-500 hover:text-gray-300'}`}
            data-testid={`section-${s.id}`}>
            <s.icon className="w-4 h-4" /> {s.label}
          </button>
        ))}
      </div>

      {/* Branding Section */}
      {activeSection === 'branding' && (
        <div className="space-y-5" data-testid="branding-section">
          {/* Live Preview */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Eye className="w-4 h-4" /> Live Preview</h3>
            <div className="rounded-xl p-4" style={{ background: '#0B0E14', border: `2px solid ${primaryColor}30` }}>
              <div className="flex items-center gap-3 mb-3">
                {getLogoUrl() ? (
                  <img src={getLogoUrl()} alt="Logo" className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold" style={{ background: `${primaryColor}20`, color: primaryColor }}>
                    {name?.charAt(0) || 'T'}
                  </div>
                )}
                <div>
                  <h4 className="font-bold text-lg" style={{ color: primaryColor }}>{name || 'Your Tenant'}</h4>
                  <p className="text-xs text-gray-500">/{tenant?.slug}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: primaryColor, color: '#000' }}>Deposit</div>
                <div className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: secondaryColor, color: '#000' }}>Withdraw</div>
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Image className="w-4 h-4" /> Logo</h3>
            <div className="flex items-center gap-4">
              {getLogoUrl() ? (
                <img src={getLogoUrl()} alt="Current logo" className="w-16 h-16 rounded-xl object-cover border border-white/10" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-[#0B0E14] flex items-center justify-center text-gray-500 border border-white/10">
                  <Image className="w-6 h-6" />
                </div>
              )}
              <div>
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="border-white/10" data-testid="upload-logo-btn">
                  {uploading ? <div className="spinner w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading...' : 'Upload Logo'}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files?.[0])} />
                <p className="text-[10px] text-gray-500 mt-1">PNG, JPG. Recommended 200x200px</p>
              </div>
            </div>
          </div>

          {/* Name & Colors */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><Type className="w-4 h-4" /> Name & Colors</h3>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Platform Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="Your platform name" data-testid="tenant-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Primary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-white/10" />
                  <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="bg-[#0B0E14] border-white/10 font-mono text-xs" data-testid="primary-color" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Secondary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-white/10" />
                  <Input value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="bg-[#0B0E14] border-white/10 font-mono text-xs" data-testid="secondary-color" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Welcome Message (shown on login)</label>
              <Input value={welcomeMessage} onChange={(e) => setWelcomeMessage(e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="Welcome to our platform!" data-testid="welcome-message" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Support Email</label>
              <Input value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="support@yourplatform.com" data-testid="support-email" />
            </div>
            <Button onClick={saveBranding} disabled={saving} className="bg-[#D4AF37] text-black w-full" data-testid="save-branding-btn">
              {saving ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save Branding'}
            </Button>
          </div>
        </div>
      )}

      {/* Rules Section */}
      {activeSection === 'rules' && (
        <div className="space-y-5" data-testid="rules-section">
          {/* Deposit Rules */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-green-400" /> Deposit Rules</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Min Deposit ($)</label>
                <Input type="number" value={rules.deposit_rules.min_amount} onChange={(e) => updateRule('deposit_rules', 'min_amount', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="dep-min" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Max Deposit ($)</label>
                <Input type="number" value={rules.deposit_rules.max_amount} onChange={(e) => updateRule('deposit_rules', 'max_amount', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="dep-max" />
              </div>
            </div>
          </div>

          {/* Withdrawal Rules */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-red-400" /> Withdrawal Rules</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Min Withdrawal ($)</label>
                <Input type="number" value={rules.withdrawal_rules.min_amount} onChange={(e) => updateRule('withdrawal_rules', 'min_amount', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="wit-min" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Max Withdrawal ($)</label>
                <Input type="number" value={rules.withdrawal_rules.max_amount} onChange={(e) => updateRule('withdrawal_rules', 'max_amount', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="wit-max" />
              </div>
            </div>
          </div>

          {/* Bonus Rules */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><Gift className="w-4 h-4 text-purple-400" /> Bonus Rules</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">First Deposit Bonus %</label>
                <Input type="number" value={rules.bonus_rules.first_deposit_bonus_pct} onChange={(e) => updateRule('bonus_rules', 'first_deposit_bonus_pct', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="bonus-first-dep" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Signup Bonus (FP)</label>
                <Input type="number" value={rules.bonus_rules.signup_bonus_xp} onChange={(e) => updateRule('bonus_rules', 'signup_bonus_xp', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="bonus-signup" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Referral Bonus (FP)</label>
                <Input type="number" value={rules.bonus_rules.referral_bonus_xp} onChange={(e) => updateRule('bonus_rules', 'referral_bonus_xp', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="bonus-referral" />
              </div>
            </div>
          </div>

          <Button onClick={saveRules} disabled={savingRules} className="bg-[#D4AF37] text-black w-full" data-testid="save-rules-btn">
            {savingRules ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {savingRules ? 'Saving...' : 'Save Rules'}
          </Button>
        </div>
      )}

      {/* Support Channels Section */}
      {activeSection === 'support' && (
        <div className="space-y-5" data-testid="support-section">
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-blue-400" /> Support & Community Links</h3>
            <p className="text-xs text-gray-500">These links show in player support menus and the landing footer when configured.</p>
            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                Facebook Page URL
              </label>
              <Input value={rules.support_channels.facebook} onChange={(e) => updateRule('support_channels', 'facebook', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="https://facebook.com/yourpage" data-testid="support-facebook" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#25D366"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2z" /></svg>
                WhatsApp Number / Channel Link
              </label>
              <Input value={rules.support_channels.whatsapp} onChange={(e) => updateRule('support_channels', 'whatsapp', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="+1234567890 or https://whatsapp.com/channel/..." data-testid="support-whatsapp" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#0088CC"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /></svg>
                Telegram Username / Link
              </label>
              <Input value={rules.support_channels.telegram} onChange={(e) => updateRule('support_channels', 'telegram', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="@yourtelegram or https://t.me/..." data-testid="support-telegram" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#006AFF"><path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.81c.02.63.66 1.03 1.24.78l2.02-.89c.17-.08.36-.1.55-.06.88.24 1.82.37 2.72.37 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2z" /></svg>
                Messenger Link
              </label>
              <Input value={rules.support_channels.messenger} onChange={(e) => updateRule('support_channels', 'messenger', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="https://m.me/..." data-testid="support-messenger" />
            </div>
            <Button onClick={saveRules} disabled={savingRules} className="bg-[#D4AF37] text-black w-full" data-testid="save-support-btn">
              {savingRules ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {savingRules ? 'Saving...' : 'Save Support Channels'}
            </Button>
          </div>
        </div>
      )}

      {/* Zoho Mail Section */}
      {activeSection === 'mail' && (
        <div className="space-y-5" data-testid="tenant-mail-section">
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#D4AF37]" /> Zoho Mail for This Tenant
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Signup codes, password reset codes, and player emails use this tenant sender first.
                </p>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full border ${mailStatus?.configured ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-yellow-500/40 text-yellow-300 bg-yellow-500/10'}`}>
                {mailStatus?.configured ? (mailStatus?.inherited ? 'Using Global Mail' : 'Tenant Mail Active') : 'Needs Password'}
              </span>
            </div>

            {mailStatus?.inherited && (
              <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-100">
                This tenant is currently using the master/global mail setup. Save below to give this tenant its own Zoho sender.
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <MailInput label="SMTP Host" value={mailConfig.smtp_host} onChange={v => setMailConfig(prev => ({ ...prev, smtp_host: v }))} placeholder="smtppro.zoho.in" />
              <MailInput label="SMTP Port" value={mailConfig.smtp_port} onChange={v => setMailConfig(prev => ({ ...prev, smtp_port: v }))} placeholder="587" />
              <MailInput label="Zoho Username" value={mailConfig.smtp_user} onChange={v => setMailConfig(prev => ({ ...prev, smtp_user: v }))} placeholder="verify@playemp.vip" />
              <MailInput label="From Email" value={mailConfig.smtp_from} onChange={v => setMailConfig(prev => ({ ...prev, smtp_from: v }))} placeholder="verify@playemp.vip" />
              <MailInput label="From Name" value={mailConfig.smtp_from_name} onChange={v => setMailConfig(prev => ({ ...prev, smtp_from_name: v }))} placeholder={name || 'Starlight'} />
              <MailInput label="Zoho App Password" type="password" value={mailConfig.smtp_pass} onChange={v => setMailConfig(prev => ({ ...prev, smtp_pass: v }))} placeholder="Paste Zoho app password" />
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4 space-y-3">
              <p className="text-xs text-gray-400">
                For this Zoho India setup use host <span className="text-[#D4AF37]">smtppro.zoho.in</span>, port <span className="text-[#D4AF37]">587</span>, username <span className="text-[#D4AF37]">verify@playemp.vip</span>.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@email.com" className="bg-[#05060A] border-white/10" />
                <Button onClick={sendTestMail} className="bg-[#131822] border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">Send Test</Button>
              </div>
            </div>

            <Button onClick={saveMailConfig} disabled={mailSaving} className="bg-[#D4AF37] text-black w-full" data-testid="save-tenant-mail-btn">
              {mailSaving ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {mailSaving ? 'Saving...' : 'Save Zoho Mail'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function MailInput({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="text-xs text-gray-400 mb-1 block">{label}</label>
      <Input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#0B0E14] border-white/10"
      />
    </div>
  );
}
